import React, { useEffect, useState, useMemo } from "react";
import { MapPin } from "lucide-react";
import { UbicacionesAPI } from "../lib/ubicaciones";

/**
 * Selector dependiente Región → Provincia → Distrito.
 * Fuente única: /api/ubicaciones/* (25 regiones · 196 provincias · 1,893 distritos INEI).
 *
 * Props:
 *  - value: { region, provincia, distrito }
 *  - onChange: (nuevoValor) => void  // { region, provincia, distrito }
 *  - required: boolean
 *  - compact: si true, oculta labels (útil en barras de filtros)
 *  - testIdPrefix: prefijo para data-testid (ej: 'conexion-publicar')
 */
export default function UbicacionSelector({
  value: valueProp = { region: "", provincia: "", distrito: "" },
  onChange,
  required = false,
  compact = false,
  disabled = false,
  testIdPrefix = "ubicacion",
  showTotals = false,
}) {
  // Normaliza value: puede llegar null si el usuario aún no eligió ubicación
  // o mientras los datos del perfil están cargando (evita crash con .region).
  const value = valueProp || { region: "", provincia: "", distrito: "" };
  const [regiones, setRegiones] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [totals, setTotals] = useState(null);

  // Carga inicial de regiones (una sola vez)
  useEffect(() => {
    UbicacionesAPI.regiones().then(setRegiones);
    if (showTotals) UbicacionesAPI.totales().then(setTotals);
  }, [showTotals]);

  // Cuando cambia región → cargar provincias
  useEffect(() => {
    if (value.region) {
      UbicacionesAPI.provincias(value.region).then(setProvincias);
    } else {
      setProvincias([]);
    }
  }, [value.region]);

  // Cuando cambia provincia → cargar distritos
  useEffect(() => {
    if (value.region && value.provincia) {
      UbicacionesAPI.distritos(value.region, value.provincia).then(setDistritos);
    } else {
      setDistritos([]);
    }
  }, [value.region, value.provincia]);

  const inputCls =
    "w-full h-12 px-3 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none disabled:bg-slate-50 disabled:text-slate-400 text-sm";
  const labelCls = "text-[11px] font-bold uppercase tracking-wider text-slate-500";

  const handle = (field, v) => {
    if (field === "region") onChange({ region: v, provincia: "", distrito: "" });
    else if (field === "provincia") onChange({ ...value, provincia: v, distrito: "" });
    else onChange({ ...value, distrito: v });
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-ubicacion`}>
      {!compact && (
        <div className="flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
            <MapPin size={14} className="text-brand-red" /> Ubicación {required ? "*" : ""}
          </label>
          {showTotals && totals && (
            <p className="text-[10px] text-slate-400 font-medium">
              Perú · {totals.total_regiones} regiones · {totals.total_provincias} provincias · {totals.total_distritos.toLocaleString("es-PE")} distritos
            </p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          {!compact && <span className={labelCls}>Región</span>}
          <select
            required={required}
            disabled={disabled}
            value={value.region || ""}
            onChange={(e) => handle("region", e.target.value)}
            data-testid={`${testIdPrefix}-region`}
            className={inputCls + (compact ? "" : " mt-1")}
          >
            <option value="">Selecciona una región</option>
            {regiones.map((r) => (
              <option key={r.codigo} value={r.nombre}>{r.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          {!compact && <span className={labelCls}>Provincia</span>}
          <select
            required={required}
            disabled={disabled || !value.region}
            value={value.provincia || ""}
            onChange={(e) => handle("provincia", e.target.value)}
            data-testid={`${testIdPrefix}-provincia`}
            className={inputCls + (compact ? "" : " mt-1")}
          >
            <option value="">
              {value.region ? "Selecciona una provincia" : "Selecciona primero la región"}
            </option>
            {provincias.map((p) => (
              <option key={p.codigo} value={p.nombre}>{p.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          {!compact && <span className={labelCls}>Distrito</span>}
          <select
            required={required}
            disabled={disabled || !value.provincia}
            value={value.distrito || ""}
            onChange={(e) => handle("distrito", e.target.value)}
            data-testid={`${testIdPrefix}-distrito`}
            className={inputCls + (compact ? "" : " mt-1")}
          >
            <option value="">
              {value.provincia ? "Selecciona un distrito" : "Selecciona primero la provincia"}
            </option>
            {distritos.map((d) => (
              <option key={d.codigo} value={d.nombre}>{d.nombre}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
